import useMicrositeState, { MicrositeStateActionType } from '~/hooks/useMicrositeState';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

export interface RegisterSectionOptions {
	enabled?: boolean;
	navigate?: () => void;
}

function defaultNavigate(id: string) {
	document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function useSections() {
	const { state } = useMicrositeState();

	return state.sections ?? [];
}

export function useRegisterSection(id: string, name?: string, enabledOrOptions: boolean | RegisterSectionOptions = true) {
	const { dispatch } = useMicrositeState();
	const enabled = typeof enabledOrOptions === 'boolean' ? enabledOrOptions : (enabledOrOptions.enabled ?? true);
	const navigate = typeof enabledOrOptions === 'boolean' ? undefined : enabledOrOptions.navigate;

	useLayoutEffect(() => {
		if (!enabled || !name) {
			return;
		}

		dispatch({
			type: MicrositeStateActionType.SECTION_REGISTER,
			id,
			name,
			navigate: navigate ?? (() => defaultNavigate(id)),
		});

		return () => {
			dispatch({ type: MicrositeStateActionType.SECTION_UNREGISTER, id });
		};
	}, [dispatch, enabled, id, name, navigate]);

	return id;
}

export function useActiveSection(sectionIds?: string[]) {
	const sections = useSections();
	const ids = useMemo(() => sectionIds ?? sections.map((section) => section.id), [sectionIds, sections]);
	const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

	useEffect(() => {
		setActiveSectionId((current) => (current && ids.includes(current) ? current : null));
	}, [ids]);

	useEffect(() => {
		if (typeof window === 'undefined' || !ids.length) {
			return;
		}

		const threshold = Array.from({ length: 11 }, (_, i) => i / 10);
		const ratioMap = new Map(ids.map((id) => [id, 0]));
		const observers: IntersectionObserver[] = [];

		ids.forEach((id) => {
			const element = document.getElementById(id);

			if (!element) {
				return;
			}

			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach(({ target, intersectionRatio }) => {
						ratioMap.set(target.id, intersectionRatio);
					});

					const highest = [...ratioMap.entries()].reduce((acc, [entryId, ratio]) => (ratio > acc.ratio ? { id: entryId, ratio } : acc), {
						id: null as string | null,
						ratio: 0,
					});

					setActiveSectionId(highest.ratio > 0 ? highest.id : null);
				},
				{ threshold },
			);

			observer.observe(element);
			observers.push(observer);
		});

		return () => {
			observers.forEach((observer) => observer.disconnect());
		};
	}, [ids]);

	return activeSectionId;
}

export function useSectionNavigation(sectionIds?: string[]) {
	const sections = useSections();
	const filteredSections = useMemo(() => {
		if (!sectionIds) {
			return sections;
		}

		const allowed = new Set(sectionIds);
		return sections.filter((section) => allowed.has(section.id));
	}, [sectionIds, sections]);

	const activeSectionId = useActiveSection(filteredSections.map((section) => section.id));

	return {
		sections: filteredSections,
		activeSectionId,
	};
}
